/**
 * The shop guide — how a spaza moves onto SmartSpaza.
 *
 * Public, like /apply: the person reading it is usually standing at a till that
 * is not paired yet, so there is nobody to sign in as. It is linked from the
 * login screen for that reason.
 *
 * The owner-facing version of docs/TERMINAL-SETUP.md and docs/CASHIER-GUIDE.md.
 * When the behaviour those describe changes, this page changes with them.
 *
 * Static content only: no API calls, so it renders from the service worker's
 * cache on a till with no signal — which is exactly when somebody opens it.
 */

import { ArrowLeft, Images } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import sceneUrl from "@/assets/spaza-scene.webp";
import { Logo, Pill } from "@/components/ui";

import { Cards, Code, GuideTable, HeroChip, InfoCard, Note, Path, Steps, SyncChip } from "./blocks";
import { MANUALS } from "./manuals";

const SECTIONS = [
  { id: "machine", title: "The till machine" },
  { id: "products", title: "Bring your products over" },
  { id: "install", title: "Install it on the till" },
  { id: "pair", title: "Connect the till to your shop" },
  { id: "staff", title: "Sign your staff in" },
  { id: "checks", title: "Four checks before you start" },
  { id: "offline", title: "No signal, or the power goes" },
  { id: "printing", title: "Printing receipts" },
  { id: "daily", title: "Every day at the till" },
  { id: "lost", title: "A till is lost or stolen" },
  { id: "reference", title: "Quick reference" },
] as const;

export function GuidePage() {
  const address = window.location.origin;

  return (
    <div className="min-h-screen bg-canvas text-ink-600">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <header className="overflow-hidden bg-navy-900 text-white">
        <div className="mx-auto grid max-w-[1120px] items-end gap-6 px-4 pt-8 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-8 lg:pt-10">
          <div className="min-w-0 pb-6 lg:pb-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="inline-flex rounded-[14px] bg-white px-3.5 py-2.5">
                <Logo height={44} label="Jalusi SmartSpaza" />
              </span>
              <Link
                to="/login"
                className="inline-flex h-touch items-center gap-2 rounded-control px-3 text-sm font-medium text-brand-100 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
              >
                <ArrowLeft className="size-4" aria-hidden />
                Sign in
              </Link>
            </div>
            <h1 className="mt-7 text-balance text-[clamp(2.1rem,5vw,3.25rem)] font-extrabold leading-[1.04] tracking-tight">
              Your till, ready to sell{" "}
              <span className="text-gold-500">with or without signal.</span>
            </h1>
            <p className="mt-4 max-w-[36ch] text-lg text-brand-100">
              How to move your shop onto SmartSpaza: bring your products over, set up the till, and
              keep selling when the network drops.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2">
              <HeroChip dot="bg-gold-500">About 20 minutes per shop</HeroChip>
              <HeroChip dot="bg-money-500">5 minutes for each extra till</HeroChip>
            </ul>
          </div>
          <img
            src={sceneUrl}
            alt=""
            width={1200}
            height={833}
            className="block w-full min-w-0 rounded-t-2xl"
          />
        </div>
      </header>

      <div className="bg-gold-500 text-navy-900">
        <div className="mx-auto flex max-w-[1120px] flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 py-3.5 sm:px-6">
          <p className="font-semibold">Your SmartSpaza address</p>
          <span className="break-all font-numeric text-[1.05rem] font-bold">{address}</span>
        </div>
      </div>

      {/* ── Training manuals ─────────────────────────────────────────────── */}
      <div className="border-b border-line bg-surface">
        <div className="mx-auto grid max-w-[1120px] gap-3 px-4 py-6 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">
            Training manuals · read here, print, or download for Word
          </p>
          <Link
            to="/help"
            className="flex items-center gap-4 rounded-card border-2 border-gold-500 bg-gold-50 p-4 hover:bg-gold-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <span className="grid size-12 shrink-0 place-items-center rounded-full bg-gold-500 text-navy-900">
              <Images className="size-6" aria-hidden />
            </span>
            <span className="grid gap-0.5">
              <span className="font-bold text-ink-900">Picture guides for everyone</span>
              <span className="text-sm">
                Cashiers, managers, stock and owners. Big pictures, few words, one step at a time,
                read out loud if you like.
              </span>
            </span>
          </Link>
          <ul className="grid gap-3 sm:grid-cols-3">
            {MANUALS.map(({ slug, short, manual }) => (
              <li key={slug}>
                <Link
                  to={`/guide/${slug}`}
                  className="grid h-full gap-1 rounded-card border border-line bg-canvas p-4 hover:border-brand-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-500">
                    {short}
                  </span>
                  <span className="font-bold text-ink-900">{manual.title}</span>
                  <span className="text-sm">{manual.subtitle}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="mx-auto grid max-w-[1120px] gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12 lg:py-12">
        <nav aria-label="Contents" className="self-start lg:sticky lg:top-6">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-ink-500">
            In this guide
          </p>
          <ol className="grid gap-0.5 sm:grid-cols-2 lg:grid-cols-1">
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="flex gap-2.5 rounded-lg px-2.5 py-1.5 text-[0.92rem] leading-snug hover:bg-brand-50 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <span className="min-w-[1.2em] font-numeric text-[0.82rem] text-ink-500">
                    {i + 1}
                  </span>
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <main className="grid min-w-0 gap-14">
          <Section n={1} id="machine" kicker="Before you start">
            <p>
              SmartSpaza runs in a web browser, so any machine with a current browser can be your
              till, including the computer you use now. Nothing extra needs to be bought.
            </p>
            <GuideTable
              head={["Machine", "Works?", "Good to know"]}
              rows={[
                [
                  "Windows PC or laptop",
                  <Pill key="a" tone="success">
                    Yes
                  </Pill>,
                  "Use Chrome or Edge. Best if you already have a USB receipt printer.",
                ],
                [
                  "Android tablet, 8″ or bigger",
                  <Pill key="b" tone="success">
                    Yes
                  </Pill>,
                  "The cheapest option. Use Chrome, held sideways.",
                ],
                [
                  "iPad",
                  <Pill key="c" tone="warn" className="whitespace-nowrap">
                    With care
                  </Pill>,
                  "It works, but the iPad is more likely to delete sales that are waiting to upload.",
                ],
                [
                  "Internet Explorer",
                  <Pill key="d" tone="danger">
                    No
                  </Pill>,
                  "Too old. Install Chrome or Edge.",
                ],
              ]}
            />
            <Note tone="gold" title="You need internet for setup.">
              Setup needs a connection: the first time you open SmartSpaza, connecting the till and
              loading your products. After that the till keeps selling without it.
            </Note>
          </Section>

          <Section n={2} id="products" kicker="Moving from another till system">
            <p>
              If you already use a till program such as Aronium, you don't need to type your
              products in again. Export your product list, then load it into SmartSpaza.
            </p>
            <Steps>
              <>
                <strong>Back up your old till first.</strong> Keep a copy of its data before you
                change anything.
              </>
              <>
                In your old till program, <strong>export the product list</strong> to Excel or CSV.
                Most till programs have this on their Products screen.
              </>
              <>
                In SmartSpaza, go to <Path>Inventory ▸ Import</Path> and choose the file.
              </>
              <>
                <strong>Check the preview.</strong> It shows which columns it recognised and flags
                any row with a problem, such as a price it can't read or a barcode another product
                already uses. Nothing is saved until you confirm.
              </>
              <>
                Do a <strong>Stock count</strong> on the Inventory screen to enter how many of each
                item you have. The import loads products and prices, not quantities.
              </>
            </Steps>
            <p>You don't need to rename your columns. SmartSpaza recognises the usual headings:</p>
            <GuideTable
              head={["SmartSpaza needs", "It also accepts headings like"]}
              rows={[
                [
                  <>
                    Product name <Pill tone="warn">required</Pill>
                  </>,
                  "Name, Product, Description, Item",
                ],
                [
                  <>
                    Selling price <Pill tone="warn">required</Pill>
                  </>,
                  "Price, Sell price, Retail price, Unit price",
                ],
                ["Cost price", "Cost, Buy price, Purchase price, Wholesale"],
                ["Barcode", "EAN, UPC, Scan code"],
                ["SKU", "Code, Item code, Product code, Stock code"],
                ["Category", "Department, Group, Product group"],
              ]}
            />
            <p>
              Prices written the South African way, like <Code>R 1 234,50</Code>, are read
              correctly. The limit is <strong>1,000 products per file</strong>. If you have more,
              split the list into two files.
            </p>
            <Note tone="green" title="Keep your old till running for a day or two.">
              Compare the totals at cash-up. When they agree, you can stop using the old one.
            </Note>
          </Section>

          <Section n={3} id="install" kicker="On the till machine">
            <Steps>
              <>
                Open <strong>Chrome</strong> or <strong>Edge</strong> and go to{" "}
                <strong className="break-all text-ink-900">{address}</strong>. Always type the{" "}
                <Code>https://</Code> address. Offline selling will not work without it.
              </>
              <>
                <strong>Install it as an app.</strong> On a PC: browser menu ▸{" "}
                <Path>Cast, save and share</Path> ▸ <Path>Install page as app</Path>. On Android:
                menu ▸ <Path>Install app</Path> or <Path>Add to Home screen</Path>.
              </>
              <>
                Close the browser. From now on, <strong>open SmartSpaza from its icon</strong> on
                the desktop or home screen, never from a browser tab.
              </>
            </Steps>
            <Note tone="amber" title="This is the step people skip.">
              Installing it is what keeps sales safe while you are offline. Installed, SmartSpaza
              opens in its own window, the browser promises to keep its data, and nobody can close
              it by accident while tidying up browser tabs. A bookmark doesn't give you any of that.
            </Note>
          </Section>

          <Section n={4} id="pair" kicker="Owner">
            <Steps>
              <>
                On another device, sign in as the owner and go to{" "}
                <Path>Settings ▸ Tills &amp; devices ▸ Register a terminal</Path>.
              </>
              <>
                Give it a name people would say out loud, like <em>Front counter</em> or{" "}
                <em>Takeaway</em>, and choose its till.
              </>
              <>
                You get a <strong>six-digit pairing code</strong>. Type it on the till's sign-in
                screen and tap <strong>Register terminal</strong>. That's it: the till stays
                connected, even after restarts.
              </>
            </Steps>
            <Note tone="gold" title="The code lasts 15 minutes and works once.">
              Get it while you are standing at the till. If it expires, ask for a new one on the
              same screen.
            </Note>
          </Section>

          <Section n={5} id="staff" kicker="Cashiers">
            <p>
              Cashiers don't need an email address or a password. Each one gets a{" "}
              <strong>staff code and a PIN</strong>, which the owner sets in{" "}
              <Path>Settings ▸ Users</Path>.
            </p>
            <p>
              To sign in at the till, the cashier taps <strong>Till</strong>, types their staff code
              and PIN, and taps <strong>Open POS</strong>.
            </p>
          </Section>

          <Section n={6} id="checks" kicker="Don't skip these">
            <Cards>
              <InfoCard tag="Check 1" title="Leave it online for a minute">
                Your products, prices and pictures download onto the till. If you unplug it straight
                away, the screen will be empty the first time the signal drops.
              </InfoCard>
              <InfoCard tag="Check 2" title="Test a sale offline">
                Turn the wifi off. Ring up a real cash sale and complete it. Turn the wifi back on
                and watch the status go green. Do this on every till.
              </InfoCard>
              <InfoCard tag="Check 3" title="Look for a storage warning">
                Tap the status chip at the top. If an amber panel says the browser has not promised
                to keep this till's data, the app was opened in a browser tab instead of installed,
                or the device is nearly full.
              </InfoCard>
              <InfoCard tag="Check 4" title="Stop anything that clears data">
                Turn off "clear history on exit", cleaner apps and shared profiles that reset. Any
                of them can delete sales that haven't uploaded yet.
              </InfoCard>
            </Cards>
          </Section>

          <Section n={7} id="offline" kicker="Load-shedding, no data, network down">
            <p>
              <strong>Keep selling.</strong> Products, prices, change and receipts all keep working.
              The status chip at the top shows what is happening:
            </p>
            <div className="flex flex-wrap gap-2.5" aria-hidden>
              <SyncChip tone="success">Online · all sent</SyncChip>
              <SyncChip tone="warn">Offline · 12 sales waiting</SyncChip>
            </div>
            <p>
              When the signal comes back, the waiting sales upload by themselves, usually within a
              minute.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Note tone="green" title="Do">
                <ul className="grid list-disc gap-1.5 pl-4">
                  <li>Keep ringing up sales as normal.</li>
                  <li>Let the till charge, or keep it plugged in.</li>
                  <li>Tap the chip if it stays amber for an hour after the signal is back.</li>
                </ul>
              </Note>
              <Note tone="red" title="Don't">
                <ul className="grid list-disc gap-1.5 pl-4">
                  <li>
                    Enter the sales again when the signal returns. They will arrive by themselves,
                    and entering them twice counts them twice.
                  </li>
                  <li>Clear the browser data or use a private window.</li>
                </ul>
              </Note>
            </div>
            <p>
              With no signal, a till can still give <strong>cash</strong> back on its own sales from
              the last week, up to the limit the owner sets in Settings ▸ Business. A cashier who
              may not refund on their own asks a manager to type their staff code and PIN, which
              works offline for a manager who has signed in on that till with signal. Card refunds,
              refunds of another till&apos;s sales and selling on account need signal: take the
              customer&apos;s number and do it when the connection is back.
            </p>
            <h3 className="mt-2 text-lg font-bold text-ink-900">
              If the power dies or the app closes
            </h3>
            <GuideTable
              head={["What", "What happens"]}
              rows={[
                [
                  "Completed sales",
                  <>
                    <Pill key="a" tone="success">
                      Safe
                    </Pill>{" "}
                    A sale is saved on the till before its receipt appears. When the till comes back
                    on, it is still waiting to upload.
                  </>,
                ],
                [
                  "The sale being rung up",
                  <>
                    <Pill key="b" tone="success">
                      Kept
                    </Pill>{" "}
                    The items are still in the basket when the same cashier signs back in. Check
                    them with the customer before taking payment.
                  </>,
                ],
                [
                  "Open shift",
                  <>
                    <Pill key="c" tone="success">
                      Safe
                    </Pill>{" "}
                    The shift is still open when the till comes back.
                  </>,
                ],
                [
                  "The cashier",
                  <>
                    <Pill key="d" tone="success">
                      Signs in again
                    </Pill>{" "}
                    Open SmartSpaza from its icon. The cashier may need their PIN again, which works
                    without signal.
                  </>,
                ],
              ]}
            />
            <Note tone="amber" title="Each cashier needs signal for their first sign-in.">
              A till checks a PIN without signal only for cashiers who have signed in on that same
              till with signal in the last 30 days. When you set up a till, have every cashier sign
              in on it once while there is signal.
            </Note>
            <p>
              Open the till with signal <strong>at least once a day</strong>. That keeps its prices
              and products up to date for the next time the network drops.
            </p>
          </Section>

          <Section n={8} id="printing" kicker="Hardware">
            <p>
              SmartSpaza prints receipts for <strong>80 mm thermal paper</strong> through your
              computer's normal printing. If your receipt printer already works with your old till,
              it will work here too.
            </p>
            <Steps>
              <>Make sure the printer's driver is installed on the till machine.</>
              <>
                Set the receipt printer as the <strong>default printer</strong>.
              </>
              <>Print a test receipt from a completed sale.</>
            </Steps>
            <GuideTable
              head={["Feature", "Status"]}
              rows={[
                [
                  "Printing receipts",
                  <Pill key="a" tone="success">
                    Works
                  </Pill>,
                ],
                [
                  "Barcode scanner",
                  <Pill key="b" tone="success">
                    Works
                  </Pill>,
                ],
                [
                  "Cash drawer opening by itself",
                  <Pill key="c" tone="warn">
                    Not yet
                  </Pill>,
                ],
                [
                  "Automatic paper cut",
                  <Pill key="d" tone="warn">
                    Not yet
                  </Pill>,
                ],
                [
                  "Scale and customer display",
                  <Pill key="e" tone="warn">
                    Not yet
                  </Pill>,
                ],
              ]}
            />
            <p>
              If your shop relies on the cash drawer opening by itself, tell us your printer model
              before you switch.
            </p>
          </Section>

          <Section n={9} id="daily" kicker="For cashiers">
            <Cards>
              <InfoCard tag="Morning" title="Open the shift">
                Tap <strong>Open shift</strong> in the amber bar, count the float in the drawer and
                type it in.
              </InfoCard>
              <InfoCard tag="All day" title="Sell">
                Tap the product or scan it. Tap <Code>Cash F10</Code> or <Code>Card F11</Code>,
                enter what the customer gave, then <strong>Complete</strong>. The till works out the
                change.
              </InfoCard>
              <InfoCard tag="When needed" title="Put a sale on hold">
                Customer gone to fetch money? Tap <strong>Hold</strong> and serve the next person.
                Tap <strong>Held</strong> and <strong>Resume</strong> when they are back. It works
                without signal, on the till that held it.
              </InfoCard>
              <InfoCard tag="When needed" title="Give a refund">
                Open the sale from <strong>Recent sales</strong> or <strong>Find a receipt</strong>,
                tap <strong>Refund</strong> and choose the items. If you may not refund on your own,
                a manager types their staff code and PIN.
              </InfoCard>
              <InfoCard tag="When needed" title="Record money taken out">
                Paid the bread delivery from the till? Record it as a pay-out at the time, so it
                doesn't show up as a shortage later.
              </InfoCard>
              <InfoCard tag="Closing" title="Cash up">
                Tap the shift bar, then <strong>Cash up</strong>, and enter what each note and coin
                adds up to. The difference shows only after you submit, so the count stays a real
                count.
              </InfoCard>
            </Cards>
          </Section>

          <Section n="!" id="lost" kicker="If it happens">
            <p>
              Go to <Path>Settings ▸ Tills &amp; devices</Path> and <strong>suspend</strong> it.
              Nobody can sign in on it or upload from it anymore. Sales it already uploaded are
              safe.
            </p>
            <p>
              Sales it hadn't uploaded yet are lost with the device. That's why the status chip
              should never stay amber for days. A replacement till takes a new pairing code and five
              minutes.
            </p>
          </Section>

          <Section n="?" id="reference" kicker="Keep this near the till">
            <GuideTable
              rows={[
                [
                  "Connect a new till",
                  <>
                    <Path>Settings ▸ Tills &amp; devices</Path>. Six digits, 15 minutes, works once.
                  </>,
                ],
                [
                  "Cashier sign-in",
                  <>
                    Staff code and PIN, on the <strong>Till</strong> tab.
                  </>,
                ],
                [
                  "Forgotten PIN",
                  <>
                    A manager resets it in <Path>Settings ▸ Users</Path>.
                  </>,
                ],
                [
                  "Load products",
                  <>
                    <Path>Inventory ▸ Import</Path>, Excel or CSV, up to 1,000 rows.
                  </>,
                ],
                ["Sales waiting to upload", "Tap the status chip to open the Sync Centre."],
                ["Lost till", <Path>Settings ▸ Tills &amp; devices ▸ Suspend</Path>],
              ]}
            />
            <div className="grid gap-2 rounded-2xl bg-navy-900 p-6 text-white">
              <h3 className="text-2xl font-extrabold">Never share your PIN</h3>
              <p className="text-brand-100">
                Nobody from SmartSpaza will ever ask for your PIN, by phone, by message or in
                person. A manager can reset a PIN without knowing it. Anyone who asks for it is not
                from SmartSpaza.
              </p>
            </div>
          </Section>
        </main>
      </div>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-[1120px] flex-wrap justify-between gap-x-6 gap-y-2 px-4 pb-10 pt-6 text-sm text-ink-500 sm:px-6">
          <span>Jalusi SmartSpaza · Point of sale for spaza shops, takeaways and mini-markets</span>
          <Link to="/apply" className="font-medium text-brand-500 hover:underline">
            Apply for your shop
          </Link>
        </div>
      </footer>
    </div>
  );
}

// ── Building blocks ─────────────────────────────────────────────────────────

function Section({
  n,
  id,
  kicker,
  children,
}: {
  n: number | string;
  id: (typeof SECTIONS)[number]["id"];
  kicker: string;
  children: ReactNode;
}) {
  const title = SECTIONS.find((s) => s.id === id)!.title;
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="grid min-w-0 scroll-mt-6 gap-4">
      <div className="flex items-start gap-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-brand-500 text-xl font-extrabold text-white">
          {n}
        </span>
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-brand-500">
            {kicker}
          </span>
          <h2
            id={`${id}-title`}
            className="text-balance text-[1.6rem] font-extrabold leading-tight text-ink-900 sm:text-[1.85rem]"
          >
            {title}
          </h2>
        </div>
      </div>
      <div className="grid max-w-[68ch] gap-4 text-[1.02rem] leading-relaxed">{children}</div>
    </section>
  );
}
